import { sanitizeHTML } from './sanitize';

export interface ClinicalDocumentPrintData {
  title: string;
  content: string;
  doctorName: string;
  specialty?: string;
  appointmentDate?: string;
  documentDate: string;
}

export function sanitizeClinicalDocumentHtml(content: string): string {
  return sanitizeHTML(content || '');
}

export function buildClinicalDocumentHtml(
  data: ClinicalDocumentPrintData,
): string {
  const content = sanitizeClinicalDocumentHtml(data.content);
  const safe = (value?: string) => escapeHtml(value || '—');

  return `<!doctype html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${safe(data.title)}</title>
    <style>
      @page { size: A4; margin: 18mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #172033; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 12pt; line-height: 1.55; }
      main { width: 100%; }
      header { border-bottom: 2px solid #0f766e; padding-bottom: 14px; margin-bottom: 22px; }
      h1 { color: #0f766e; font-size: 22pt; margin: 0 0 12px; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; font-size: 10.5pt; }
      .meta strong { display: block; color: #475569; font-size: 8.5pt; text-transform: uppercase; letter-spacing: .04em; }
      .content { overflow-wrap: anywhere; white-space: pre-wrap; }
      .content p { margin: 0 0 12px; }
      .content ul, .content ol { margin: 8px 0 12px; padding-left: 24px; }
      .content h1, .content h2, .content h3 { color: #172033; margin: 18px 0 8px; }
      .content a { color: inherit; text-decoration: none; }
      footer { margin-top: 42px; border-top: 1px solid #cbd5e1; padding-top: 12px; color: #64748b; font-size: 9pt; }
      @media print {
        html, body, main { visibility: visible !important; display: block !important; }
        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <main>
      <header>
        <h1>${safe(data.title)}</h1>
        <div class="meta">
          <div><strong>Médico</strong>${safe(data.doctorName)}</div>
          <div><strong>Especialidade</strong>${safe(data.specialty)}</div>
          <div><strong>Data da consulta</strong>${safe(data.appointmentDate)}</div>
          <div><strong>Data do documento</strong>${safe(data.documentDate)}</div>
        </div>
      </header>
      <section class="content">${content}</section>
      <footer>Documento clínico emitido pela plataforma App Saúde.</footer>
    </main>
  </body>
</html>`;
}

export function printClinicalDocument(data: ClinicalDocumentPrintData): void {
  const html = buildClinicalDocumentHtml(data);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    schedulePrint(printWindow);
    return;
  }

  // Fallback para bloqueadores de popup: imprime um iframe isolado.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Documento para impressão');
  iframe.style.position = 'fixed';
  iframe.style.right = '100%';
  iframe.style.bottom = '0';
  iframe.style.width = '210mm';
  iframe.style.height = '297mm';
  iframe.style.border = '0';
  iframe.srcdoc = html;
  iframe.onload = () => {
    const target = iframe.contentWindow;
    if (!target) {
      iframe.remove();
      return;
    }
    schedulePrint(target, () => iframe.remove());
  };
  document.body.appendChild(iframe);
}

function schedulePrint(target: Window, afterPrint?: () => void): void {
  const run = async () => {
    try {
      await target.document.fonts?.ready;
    } catch {
      // Font loading is best-effort; system fonts are always available.
    }

    target.requestAnimationFrame(() => {
      target.requestAnimationFrame(() => {
        target.focus();
        target.print();
        if (afterPrint) {
          window.setTimeout(afterPrint, 500);
        }
      });
    });
  };

  if (target.document.readyState === 'complete') {
    void run();
  } else {
    target.addEventListener('load', () => void run(), { once: true });
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
